/**
 * Contact properties in databases (a project's "Clients"): the cell's chips (each opens the person's contact card),
 * the picker that edits it (the workspace's mail contacts), and the row page's "Recent emails with these clients".
 */
import type { PersonOption } from "@bundle/core";
import { Shell } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import type { MailContact } from "@bundle/contracts";
import { useEffect, useMemo, useSyncExternalStore } from "react";

const NONE: MailContact[] = [];
const noSubscribe = () => () => {};
import { Avatar, Button, Stack, Text } from "../primitives/index.ts";
import { useNavigation } from "../context/AppContext.tsx";
import { useViewModel, useWorkspace } from "../hooks/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { PeoplePicker } from "../components/database/PeoplePicker.tsx";
import { PersonName } from "./ContactCard.tsx";
import { useMail, type MailServices } from "./registry.ts";

/** The contacts in a cell: avatar and name, the name opening the card. Without Email, how many there are. */
export function ContactChips({ contactIds, compact, testID }: { contactIds: string[]; compact?: boolean | undefined; testID?: string | undefined }) {
  const mail = useMail();
  const t = useT();
  if (mail === undefined) {
    return (
      <Text variant="footnote" color="text.secondary" testID={testID}>
        {t("db.contact.count", { count: contactIds.length })}
      </Text>
    );
  }
  return <Chips mail={mail} contactIds={contactIds} compact={compact === true} testID={testID} />;
}

function Chips({ mail, contactIds, compact, testID }: { mail: MailServices; contactIds: string[]; compact: boolean; testID: string | undefined }) {
  const ws = useWorkspace();
  const s = useViewModel(mail.contacts);
  useEffect(() => mail.contacts.ensureLoaded(), [mail.contacts]);
  return (
    <Stack direction="horizontal" align="center" gap="2" wrap testID={testID}>
      {contactIds.map((id, i) => {
        const c = s.all.find((x) => x.id === id);
        if (c === undefined) return null;
        const name = c.name ?? c.email;
        return (
          <Stack key={id} direction="horizontal" align="center" gap="1">
            <Avatar name={name} src={Mail.mailAvatarPath(ws.vm.wsId, c.email)} size="avatar.xs" />
            <PersonName address={{ address: c.email, name }} text={name} variant="footnote" testID={`${testID ?? "contact"}-${compact ? "c" : "p"}-${i}`} />
          </Stack>
        );
      })}
    </Stack>
  );
}

/** The workspace's contacts as filter choices (none without Email). */
export function useContactChoices(): { id: string; label: string }[] {
  const vm = useMail()?.contacts;
  const all = useSyncExternalStore(vm?.subscribe ?? noSubscribe, () => vm?.getSnapshot().all ?? NONE);
  useEffect(() => vm?.ensureLoaded(), [vm]);
  return useMemo(() => all.filter((c) => c.userId === undefined).map((c) => ({ id: c.id, label: c.name ?? c.email })), [all]);
}

/** Picks contacts for a contact cell (search and toggle, like a person cell). */
export function ContactPicker({ selected, onChange, onDone, testID }: { selected: string[]; onChange: (ids: string[]) => void; onDone: () => void; testID: string }) {
  const mail = useMail();
  if (mail === undefined) return null;
  return <Picker mail={mail} selected={selected} onChange={onChange} onDone={onDone} testID={testID} />;
}

function Picker({ mail, selected, onChange, onDone, testID }: { mail: MailServices; selected: string[]; onChange: (ids: string[]) => void; onDone: () => void; testID: string }) {
  const ws = useWorkspace();
  const s = useViewModel(mail.contacts);
  useEffect(() => mail.contacts.ensureLoaded(), [mail.contacts]);
  // Clients first, then everyone else (members are people, not contacts here).
  const sorted = [...s.all.filter((c) => c.userId === undefined && c.kind === "client"), ...s.all.filter((c) => c.userId === undefined && c.kind !== "client")];
  const options: PersonOption[] = sorted.map((c) => ({ userId: c.id, name: c.name ?? c.email, avatarUrl: Mail.mailAvatarPath(ws.vm.wsId, c.email), isAgent: false }));
  return <PeoplePicker people={options} selected={selected} onChange={onChange} onDone={onDone} testID={testID} />;
}

/** "Recent emails with these clients": Mail searched for conversations with any of them (`with:`). */
export function RecentEmailsLink({ contactIds, testID }: { contactIds: string[]; testID: string }) {
  const mail = useMail();
  if (mail === undefined || contactIds.length === 0) return null;
  return <Link mail={mail} contactIds={contactIds} testID={testID} />;
}

function Link({ mail, contactIds, testID }: { mail: MailServices; contactIds: string[]; testID: string }) {
  const t = useT();
  const ws = useWorkspace();
  const nav = useNavigation();
  const s = useViewModel(mail.contacts);
  useEffect(() => mail.contacts.ensureLoaded(), [mail.contacts]);
  const addresses = contactIds.map((id) => s.all.find((c) => c.id === id)?.email).filter((x): x is string => x !== undefined);
  if (addresses.length === 0) return null;
  return (
    <Stack direction="horizontal">
      <Button
        size="sm"
        variant="ghost"
        icon="mail"
        label={t("db.contact.recentEmails")}
        onPress={() => nav.open(Shell.formatUrl({ type: "mail", ws: ws.slug, q: Mail.mailWithSearch(addresses) }))}
        testID={testID}
      />
    </Stack>
  );
}
